import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ReportDraft } from '../../types/matchReport.ts';

interface ScoreStepProps {
  draft: ReportDraft;
  onChange: (_updates: Partial<ReportDraft>) => void;
  homeTeamName: string;
  awayTeamName: string;
  isHomeTeam: boolean;
  opponentReportScore?: { homeGoals: number; awayGoals: number } | null;
}

export const ScoreStep: React.FC<ScoreStepProps> = ({
  draft,
  onChange,
  homeTeamName,
  awayTeamName,
  opponentReportScore,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const handleScoreChange = (team: 'home' | 'away', value: string) => {
    const numericValue = value === '' ? undefined : parseInt(value, 10);
    
    if (team === 'home') {
      onChange({ homeGoals: numericValue });
    } else {
      onChange({ awayGoals: numericValue });
    }
  };

  const handleForfeitChange = (checked: boolean) => {
    onChange({
      isForfeit: checked,
      // Clear scores when forfeit is checked
      homeGoals: checked ? 0 : draft.homeGoals,
      awayGoals: checked ? 0 : draft.awayGoals,
    });
  };

  const handleNotesChange = (value: string) => {
    onChange({ notes: value });
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom align="center" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
        Match Score
      </Typography>
      
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
        Enter the final score for this match. You can add additional details and events in the next steps.
      </Typography>

      {opponentReportScore && (
        <Alert severity="info" sx={{ mb: 2 }}>
          The score has been pre-filled from the opponent's submitted report ({opponentReportScore.homeGoals}–{opponentReportScore.awayGoals}). Please verify it is correct before continuing.
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 1, sm: 3 } }}>
            {/* Home Team Score */}
            <Box sx={{ textAlign: 'center', minWidth: { xs: 80, sm: 120 } }}>
              <Typography gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1.25rem' }, fontWeight: 600, wordBreak: 'break-word' }}>
                {homeTeamName}
              </Typography>
             
              <TextField
                type="number"
                value={draft.homeGoals ?? ''}
                onChange={(e) => handleScoreChange('home', e.target.value)}
                disabled={draft.isForfeit}
                inputProps={{
                  min: 0,
                  max: 50,
                  style: { textAlign: 'center', fontSize: isMobile ? '1.5rem' : '2rem' }
                }}
                sx={{
                  width: { xs: 70, sm: 80 },
                  '& .MuiOutlinedInput-input': {
                    textAlign: 'center',
                  }
                }}
              />
            </Box>

            {/* VS Separator */}
            <Typography variant="h4" color="text.secondary" sx={{ mx: { xs: 0.5, sm: 2 }, fontSize: { xs: '1.25rem', sm: '2rem' } }}>
              vs
            </Typography>

            {/* Away Team Score */}
            <Box sx={{ textAlign: 'center', minWidth: { xs: 80, sm: 120 } }}>
              <Typography gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1.25rem' }, fontWeight: 600, wordBreak: 'break-word' }}>
                {awayTeamName}
              </Typography>
              
              <TextField
                type="number"
                value={draft.awayGoals ?? ''}
                onChange={(e) => handleScoreChange('away', e.target.value)}
                disabled={draft.isForfeit}
                inputProps={{
                  min: 0,
                  max: 50,
                  style: { textAlign: 'center', fontSize: isMobile ? '1.5rem' : '2rem' }
                }}
                sx={{
                  width: { xs: 70, sm: 80 },
                  '& .MuiOutlinedInput-input': {
                    textAlign: 'center',
                  }
                }}
              />
            </Box>
          </Box>

          {/* Forfeit Option */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={draft.isForfeit || false}
                  onChange={(e) => handleForfeitChange(e.target.checked)}
                />
              }
              label="This game was forfeited"
            />
          </Box>

          {draft.isForfeit && (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
              Forfeit games are recorded as 0-0. Add details in the notes section below.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Match Notes */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Match Notes (Optional)
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            value={draft.notes || ''}
            onChange={(e) => handleNotesChange(e.target.value)}
            placeholder="Add any additional notes about the match (e.g., weather conditions, referee decisions, injuries, etc.)"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography variant="caption" color="text.secondary">
                    {(draft.notes || '').length}/500
                  </Typography>
                </InputAdornment>
              )
            }}
            inputProps={{
              maxLength: 500
            }}
          />
        </CardContent>
      </Card>

      {/* Score Validation */}
      {!draft.isForfeit && (
        <Box sx={{ mt: 2 }}>
          {(draft.homeGoals === undefined || draft.awayGoals === undefined) && (
            <Typography variant="body2" color="warning.main" align="center">
              Please enter scores for both teams to continue
            </Typography>
          )}
          
          {((draft.homeGoals !== undefined && draft.homeGoals !== null && draft.homeGoals < 0) || 
           (draft.awayGoals !== undefined && draft.awayGoals !== null && draft.awayGoals < 0)) ? (
            <Typography variant="body2" color="error" align="center">
              Scores cannot be negative
            </Typography>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

export default ScoreStep;